import { useEffect, useRef, useState } from "react";
import { POSTER_BG, newRow, newFooterRow } from "@/lib/posterTemplate";
import { mediaUrl } from "@/lib/api";
import { X, Plus } from "lucide-react";
import { UscLayout } from "@/components/UscLayout";

const DEF_PRIMARY = "#182f5c";
const DEF_ACCENT = "#c6a15a";

/* ---- color utils ---- */
const hexToRgb = (h) => {
  let s = (h || "").replace("#", "");
  if (s.length === 3) s = s.split("").map((c) => c + c).join("");
  const n = parseInt(s || "000000", 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};
const toHex = (arr) =>
  "#" + arr.map((x) => Math.max(0, Math.min(255, Math.round(x))).toString(16).padStart(2, "0")).join("");
const darken = (hex, f) => toHex(hexToRgb(hex).map((x) => x * (1 - f)));
const lighten = (hex, f) => toHex(hexToRgb(hex).map((x) => x + (255 - x) * f));

const themeOf = (data) => {
  const primary = data.color_primary || DEF_PRIMARY;
  const accent = data.color_accent || DEF_ACCENT;
  return {
    navy: primary,
    navyAlt: lighten(primary, 0.12),
    navyDark: darken(primary, 0.35),
    gold: accent,
    goldSoft: lighten(accent, 0.2),
    team: (data.team_name || "").trim(),
    highlight: data.highlight_team !== false,
    highlightFirst: data.highlight_first_team !== false,
    showScores: data.show_scores === true,
  };
};

const txtStyle = (color, size, weight = 800, align = "left", italic = false, font = "Outfit, sans-serif") => ({
  color,
  fontSize: size,
  fontWeight: weight,
  fontFamily: font,
  fontStyle: italic ? "italic" : "normal",
  textAlign: align,
  lineHeight: 1.05,
  letterSpacing: "0.3px",
  textTransform: "uppercase",
  ...(font && font.includes("Bebas") ? { paddingTop: "0.11em" } : {}),
});

export const timeCell = (r, t, editable) => ({
  value: t.showScores ? r.score || (editable ? "" : r.time) : r.time,
  placeholder: t.showScores ? r.time || "–" : "",
});

/* ---- highlight helper ---- */
const splitTeam = (text, team) => {
  if (!team) return [{ text, hit: false }];
  const res = [];
  const low = (text || "").toLowerCase();
  const tl = team.toLowerCase();
  let i = 0;
  while (i < text.length) {
    const idx = low.indexOf(tl, i);
    if (idx === -1) {
      res.push({ text: text.slice(i), hit: false });
      break;
    }
    if (idx > i) res.push({ text: text.slice(i, idx), hit: false });
    res.push({ text: text.slice(idx, idx + team.length), hit: true });
    i = idx + team.length;
  }
  return res;
};

const HighlightText = ({ value, t, style, hl = true }) => {
  if (!hl || !t.highlight || !t.team) return <span style={style}>{value}</span>;
  return (
    <span style={style}>
      {splitTeam(value || "", t.team).map((p, i) =>
        p.hit ? (
          <span key={`h${i}-${p.text}`} style={{ color: t.gold }}>{p.text}</span>
        ) : (
          <span key={`n${i}-${p.text}`}>{p.text}</span>
        )
      )}
    </span>
  );
};

/* plain editable text / span */
export const T = ({ value, onChange, onBlur, editable, color, size, weight = 800, align = "left", italic, placeholder, font }) => {
  const style = txtStyle(color, size, weight, align, italic, font);
  if (!editable) return <span style={style}>{value}</span>;
  return (
    <input
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      onBlur={onBlur}
      className="w-full rounded-sm bg-transparent px-1 outline-none transition-colors focus:bg-white/20"
      style={style}
    />
  );
};

/* match text: highlighted display, click to edit when editable */
const withLogo = (value, logo, size, render) => {
  const img = <img src={mediaUrl(logo.url)} alt="" draggable={false} onError={(e) => { e.currentTarget.style.display = "none"; }}
    style={{ height: size * 1.05, width: size * 1.05, objectFit: "contain", flex: "none", background: "#ffffff", borderRadius: "50%", padding: 2, boxShadow: "0 0 0 2px rgba(198,161,90,0.9)" }} />;
  const m = (value || "").match(/\s+-\s+/);
  if (logo.side === 0 || !m) return <>{img}{render(value)}</>;
  const cut = m.index + m[0].length;
  return <>{render(value.slice(0, cut))}{img}{render(value.slice(cut))}</>;
};

export const MatchText = ({ value, onChange, editable, t, color, size, weight = 700, align = "left", hl = true, font, logo }) => {
  const [editing, setEditing] = useState(false);
  const style = txtStyle(color, size, weight, align, false, font);
  if (editable && editing) {
    return (
      <input
        autoFocus
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={() => setEditing(false)}
        onKeyDown={(e) => e.key === "Enter" && setEditing(false)}
        className="w-full rounded-sm bg-white/25 px-1 outline-none"
        style={style}
      />
    );
  }
  const node = logo ? (
    <span style={{ ...style, display: "inline-flex", alignItems: "center", gap: 6, whiteSpace: "pre" }}>
      {withLogo(value, logo, size, (v) => <HighlightText value={v} t={t} style={{ ...style, whiteSpace: "pre" }} hl={hl} />)}
    </span>
  ) : <HighlightText value={value} t={t} style={style} hl={hl} />;
  if (editable)
    return (
      <div className="w-full cursor-text px-1" style={{ textAlign: align }} onClick={() => setEditing(true)}>
        {node}
      </div>
    );
  return node;
};

const MatchRow = ({ r, i, editable, onChange, onRemove, t }) => {
  const catGold = i % 2 === 0;
  return (
    <div className="group/row relative flex items-stretch gap-1.5" style={{ minHeight: 38 }}>
      <div className="flex w-[70px] shrink-0 items-center justify-center rounded" style={{ background: catGold ? t.gold : t.navyDark }}>
        <T value={r.cat} editable={editable} onChange={(v) => onChange({ ...r, cat: v })}
           color={catGold ? t.navyDark : t.goldSoft} size={17} weight={800} align="center" />
      </div>
      <div className="flex flex-1 items-center rounded px-1" style={{ background: i % 2 === 0 ? t.navy : t.navyAlt }}>
        <MatchText value={r.match} editable={editable} onChange={(v) => onChange({ ...r, match: v })}
           t={t} color="#f4f1e8" size={17} weight={700} align="left" />
      </div>
      <div className="flex w-[84px] shrink-0 items-center justify-center rounded"
        style={{ background: t.showScores ? t.navyDark : t.gold }}>
        <T {...timeCell(r, t, editable)}
           editable={editable}
           onChange={(v) => onChange(t.showScores ? { ...r, score: v } : { ...r, time: v })}
           color={t.showScores ? t.goldSoft : t.navyDark} size={17} weight={800} align="center" />
      </div>
      {editable && (
        <button onClick={onRemove}
          className="absolute -right-6 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded-full bg-red-500 text-white opacity-0 transition-opacity group-hover/row:opacity-100"
          title="Supprimer la ligne">
          <X className="h-3 w-3" />
        </button>
      )}
    </div>
  );
};

const FooterRow = ({ r, editable, onChange, onRemove, t }) => (
  <div className="group/frow relative flex items-stretch gap-2 rounded"
    style={{ minHeight: 46, padding: t.highlightFirst ? 3 : 0, boxShadow: t.highlightFirst ? `0 0 0 2px ${t.gold}, 0 0 18px ${t.gold}66` : "none" }}>
    <div className="flex w-[90px] shrink-0 items-center justify-center rounded" style={{ background: t.navyDark }}>
      <T value={r.cat} editable={editable} onChange={(v) => onChange({ ...r, cat: v })}
         color={t.goldSoft} size={22} weight={800} align="center" />
    </div>
    <div className="flex flex-1 items-center rounded px-2" style={{ background: t.gold }}>
      <MatchText value={r.match} editable={editable} onChange={(v) => onChange({ ...r, match: v })}
         t={t} color={t.navyDark} size={22} weight={800} align="left" hl={false} />
    </div>
    <div className="flex w-[180px] shrink-0 items-center justify-center rounded px-2" style={{ background: t.navy }}>
      <T value={r.day} editable={editable} onChange={(v) => onChange({ ...r, day: v })}
         color="#f4f1e8" size={20} weight={800} align="center" />
    </div>
    <div className="flex w-[90px] shrink-0 items-center justify-center rounded"
      style={{ background: t.showScores ? t.navyDark : t.gold }}>
      <T {...timeCell(r, t, editable)} editable={editable}
         onChange={(v) => onChange(t.showScores ? { ...r, score: v } : { ...r, time: v })}
         color={t.showScores ? t.goldSoft : t.navyDark} size={22} weight={800} align="center" />
    </div>
    {editable && (
      <button onClick={onRemove}
        className="absolute -right-6 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded-full bg-red-500 text-white opacity-0 transition-opacity group-hover/frow:opacity-100"
        title="Supprimer la ligne">
        <X className="h-3 w-3" />
      </button>
    )}
  </div>
);

const EMPTY_COL = { header: "", rows: [] };
const Column = ({ col = EMPTY_COL, editable, onChange, t }) => {
  const update = (patch) => onChange({ ...col, ...patch });
  const updateRow = (ri, nr) => update({ rows: col.rows.map((r, i) => (i === ri ? nr : r)) });
  const removeRow = (ri) => update({ rows: col.rows.filter((_, i) => i !== ri) });
  const addRow = () => update({ rows: [...col.rows, newRow()] });
  return (
    <div className="flex flex-1 flex-col gap-1.5">
      <div className="mb-1 flex justify-center">
        <div className="rounded px-4 py-1" style={{ background: t.navy }}>
          <T value={col.header} editable={editable} onChange={(v) => update({ header: v })}
             color="#f4f1e8" size={22} weight={800} align="center" />
        </div>
      </div>
      {col.rows.map((r, ri) => (
        <MatchRow key={r.id} r={r} i={ri} editable={editable} t={t}
          onChange={(nr) => updateRow(ri, nr)} onRemove={() => removeRow(ri)} />
      ))}
      {editable && (
        <button onClick={addRow}
          className="mt-1 flex items-center justify-center gap-1 rounded border border-dashed border-white/40 py-1 text-white/70 transition-colors hover:border-white hover:text-white"
          style={{ fontSize: 14, fontWeight: 700 }}>
          <Plus className="h-3.5 w-3.5" /> Ligne
        </button>
      )}
    </div>
  );
};

const Crest = ({ data, editable, onChange, t }) => (
  <div className="flex w-[180px] shrink-0 flex-col items-center justify-center">
    <div className="relative flex h-[170px] w-[170px] items-center justify-center overflow-hidden rounded-full"
      style={{ background: `radial-gradient(circle at 50% 40%, ${t.navyAlt} 0%, ${t.navyDark} 100%)`, border: `4px solid ${t.gold}` }}>
      {data.logo_url ? (
        <img src={mediaUrl(data.logo_url)} alt="" className="h-full w-full rounded-full object-cover" draggable={false} />
      ) : (
        <div className="px-2 text-center">
          <T value={data.club_name} editable={editable} onChange={(v) => onChange({ ...data, club_name: v })}
             color={t.goldSoft} size={24} weight={800} align="center" />
        </div>
      )}
    </div>
  </div>
);

export const PosterContent = ({ data, editable, onChange, height }) => {
  const t = themeOf(data);
  if (data.layout === "usc") return <UscLayout data={data} editable={editable} onChange={onChange} t={t} height={height} />;
  const bg = data.background_url ? mediaUrl(data.background_url) : POSTER_BG;
  const setData = (patch) => onChange({ ...data, ...patch });
  const setCol = (ci, nc) => setData({ columns: [0, 1].map((i) => (i === ci ? nc : data.columns?.[i] || { ...EMPTY_COL, id: `col${i}` })) });
  const setFooter = (fi, nf) => setData({ footer: data.footer.map((f, i) => (i === fi ? nf : f)) });
  const removeFooter = (fi) => setData({ footer: data.footer.filter((_, i) => i !== fi) });
  const addFooter = () => setData({ footer: [...data.footer, newFooterRow()] });

  return (
    <div className="relative h-[720px] w-[1280px] overflow-hidden"
      style={{ backgroundColor: "#0a1730", backgroundImage: `url(${bg})`, backgroundSize: "cover", backgroundPosition: "center" }}>
      <div className="flex justify-center pt-4">
        <div className="rounded-lg px-10 py-2"
          style={{ background: `linear-gradient(90deg,${t.navyDark},${t.navyAlt},${t.navyDark})`, border: `2px solid ${t.gold}` }}>
          <T value={data.title} editable={editable} onChange={(v) => setData({ title: v })}
             color="#ffffff" size={46} weight={800} italic align="center" />
        </div>
      </div>

      <div className="mx-8 mt-3 rounded-xl p-3" style={{ background: "rgba(150,162,186,0.14)", backdropFilter: "blur(2px)" }}>
        <div className="flex items-start gap-4">
          <Column col={data.columns[0]} editable={editable} onChange={(nc) => setCol(0, nc)} t={t} />
          <Crest data={data} editable={editable} onChange={onChange} t={t} />
          <Column col={data.columns[1]} editable={editable} onChange={(nc) => setCol(1, nc)} t={t} />
        </div>
      </div>

      <div className="mx-8 mt-2 flex flex-col gap-2">
        {data.footer.map((f, fi) => (
          <FooterRow key={f.id} r={f} editable={editable} t={t}
            onChange={(nf) => setFooter(fi, nf)} onRemove={() => removeFooter(fi)} />
        ))}
        {editable && (
          <button onClick={addFooter}
            className="flex items-center justify-center gap-1 rounded border border-dashed border-white/40 py-1.5 text-white/70 transition-colors hover:border-white hover:text-white"
            style={{ fontSize: 15, fontWeight: 700 }}>
            <Plus className="h-4 w-4" /> Ligne (équipe première)
          </button>
        )}
      </div>
    </div>
  );
};

export const Poster = ({ data, editable = false, onChange, className = "" }) => {
  const ref = useRef(null);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const compute = () => {
      const w = el.clientWidth;
      const h = el.clientHeight;
      let s = w / 1280;
      if (h && 720 * s > h) s = h / 720;
      setScale(s || 1);
    };
    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <div ref={ref} className={`relative flex items-center justify-center overflow-hidden ${className}`}>
      <div style={{ width: 1280, height: 720, flex: "none", transform: `scale(${scale})`, transformOrigin: "center center" }}>
        <PosterContent data={data} editable={editable} onChange={onChange} />
      </div>
    </div>
  );
};
